import type { Duration } from "@bufbuild/protobuf/wkt";

import { duration } from "@/format/time";
import type { PlainEnvironment } from "@/queries/environment-queries";
import type { PlainOrganizationPolicies } from "@/queries/organization-queries";

export type TimeoutOption = {
    /** Full label for list items, e.g. "30m of inactivity" */
    label: string;
    /** Compact label for inline display, e.g. "30m inactivity" */
    shortLabel: string;
    value: string;
};

export function initialTimeoutValue(environment: PlainEnvironment): TimeoutOption {
    const timeout = toTimeoutValue(environment?.spec?.timeout?.disconnected);
    return toTimeout(timeout);
}

export function computeTimeoutOptions(
    environment: PlainEnvironment,
    policies?: PlainOrganizationPolicies,
): TimeoutOption[] {
    return computeTimeoutOptionsFromSeconds(
        toTimeoutValue(environment?.spec?.timeout?.disconnected),
        Number(policies?.maximumEnvironmentTimeout?.seconds || 0),
    );
}

/** Pure computation from scalar values — suitable for stable useMemo deps. */
export function computeTimeoutOptionsFromSeconds(
    currentTimeoutSeconds: number,
    maxTimeoutPolicySeconds: number,
): TimeoutOption[] {
    let timeouts: number[] = [30 * 60, 60 * 60, 60 * 60 * 3, 60 * 60 * 8];
    if (maxTimeoutPolicySeconds > 0) {
        timeouts = timeouts.filter((seconds) => seconds <= maxTimeoutPolicySeconds);
    }
    const customTimeout =
        currentTimeoutSeconds !== 0 && timeouts.find((seconds) => seconds === currentTimeoutSeconds) === undefined;
    if (customTimeout) {
        timeouts.push(currentTimeoutSeconds);
        timeouts.sort((a, b) => a - b);
    }
    if (maxTimeoutPolicySeconds === 0) {
        timeouts.push(0);
    }
    return timeouts.map(toTimeout);
}

export function toTimeout(seconds: number): TimeoutOption {
    if (seconds <= 0) {
        return {
            label: "Never",
            shortLabel: "never",
            value: "0",
        };
    }
    const d = duration(seconds);
    return {
        label: d + " of inactivity",
        shortLabel: d + " inactivity",
        value: String(seconds),
    };
}

export function toTimeoutValue(d?: Duration): number {
    return d === undefined ? 30 * 60 : Number(d.seconds);
}
